import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { servicesData } from "./servicesData";
import "./servicesTabs.scss";

export default function ServicesTabs({ lang, initialServiceId = "" }) {
  const services = useMemo(() => {
    return servicesData[lang].sections.flatMap((section) => section.services);
  }, [lang]);

  const initialIndex = useMemo(() => {
    if (!initialServiceId) return 0;
    const idx = services.findIndex((s) => s.id === initialServiceId);
    return idx >= 0 ? idx : 0;
  }, [services, initialServiceId]);

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [isMobile, setIsMobile] = useState(false);
  const itemsRef = useRef([]);
  const dividerRef = useRef(null);
  const sectionRef = useRef(null);
  const [lineTop, setLineTop] = useState(0);

  useEffect(() => {
    setActiveIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (!initialServiceId || !sectionRef.current) return;
    const timer = setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(timer);
  }, [initialServiceId, lang]);

  useEffect(() => {
    if (!isMobile) {
      const el = itemsRef.current[activeIndex];
      const divider = dividerRef.current;
      if (el && divider) {
        const elRect = el.getBoundingClientRect();
        const dividerRect = divider.getBoundingClientRect();
        const offset =
          elRect.top - dividerRect.top + elRect.height / 2 - 1;
        setLineTop(offset);
      }
    }
  }, [activeIndex, isMobile, services]);

  const renderDesktop = () => (
    <div className="services-wrapper">
      <div className="services-container">
        <div className="services-left">
          {services.map((service, index) => (
            <button
              key={service.id}
              type="button"
              ref={(el) => {
                itemsRef.current[index] = el;
              }}
              className={`service-item ${
                index === activeIndex ? "active" : ""
              }`}
              onClick={() => setActiveIndex(index)}
            >
              {service.title}
            </button>
          ))}
        </div>

        <div className="services-divider" ref={dividerRef}>
          <span className="vertical-line" />
          <motion.span
            className="horizontal-line"
            animate={{ top: lineTop }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          />
        </div>

        <div className="services-right">
          <AnimatePresence mode="wait">
            <motion.div
              key={services[activeIndex].id}
              className="service-content"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              {services[activeIndex].groups.map((group) => (
                <div key={group.title}>
                  <p>
                    <strong>{group.title}</strong>
                  </p>
                  <p>{group.text}</p>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );

  const renderMobile = () => (
    <div className="services-mobile">
      <div className="service-pills">
        {services.map((service, index) => (
          <motion.button
            key={service.id}
            type="button"
            className={`pill ${index === activeIndex ? "active" : ""}`}
            onClick={() => setActiveIndex(index)}
            whileTap={{ scale: 0.95 }}
          >
            {service.title}
          </motion.button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={services[activeIndex].id}
          className="mobile-content"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
        >
          {services[activeIndex].groups.map((group) => (
            <div key={group.title}>
              <p>
                <strong>{group.title}</strong>
              </p>
              <p>{group.text}</p>
            </div>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );

  return (
    <div ref={sectionRef} className="services-tabs-anchor">
      {isMobile ? renderMobile() : renderDesktop()}
    </div>
  );
}
